import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apolloClient } from '@/vendor/apollo-client.ts'
import { BACKGROUND_OPERATION } from '@/utils/backgroundOperation'
import { GET_USER_CHANGES } from '@/queries/userChanges.ts'
import { createPersistedCache } from '@/stores/sync/createPersistedCache.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { useInfoStore } from '@/stores/info.ts'
import { sendError } from '@/utils/sendError.ts'
import type { GetUserChangesQuery } from '../../../__generated__/graphql.ts'
import {
  applyPage,
  emptyData,
  entityRows,
  isCurrentSchema,
  type ApplyablePage,
  type UserSyncData,
} from './sync.ts'

type UserChangesPage = NonNullable<GetUserChangesQuery['userChanges']>
export type CollectionDeltaRow = UserChangesPage['userReleaseEntryStatuses'][number]
export type CustomListDeltaRow = UserChangesPage['userCustomLists'][number]
export type ReleaseEntryItemDeltaRow = UserChangesPage['userCustomListReleaseEntryItems'][number]
export type NoveltyItemDeltaRow = UserChangesPage['userCustomListNoveltyItems'][number]
export type UserAdminDeltaRow = UserChangesPage['userAdmins'][number]

const USER_SYNC_KEY = 'userSyncState'
const PAGE_LIMIT = 500

const cache = createPersistedCache<UserSyncData>(USER_SYNC_KEY, emptyData(null))

export const useUserSyncStore = defineStore('user-sync', () => {
  const authStore = useAuthStore()
  const infoStore = useInfoStore()

  const data = ref<UserSyncData>(emptyData(null))
  const loaded = ref(false)
  // Bumped whenever applied rows change, so derived consumers can recompute.
  const revision = ref(0)

  let inFlight: Promise<boolean> | null = null
  // Bumped whenever the active account changes (switch/reset/sign-out) so an
  // in-flight runSync bound to the previous user can detect it is stale and stop
  // applying/persisting rows into the new user's replica.
  let syncGeneration = 0

  function invalidateSync() {
    syncGeneration++
    inFlight = null
  }

  function currentUserId(): string | null {
    return authStore.authState?.userAdmin?.user?.id ?? null
  }

  // True once this user's replica carries applied rows (cursor moved off the
  // zero seq). Consumers use it to tell "the user really has nothing" from "we
  // never got anything", which a skipped or failed sync cannot distinguish.
  const hasReplica = computed<boolean>(() => {
    void revision.value
    return data.value.userId === currentUserId() && data.value.cursor !== '0'
  })

  function persist() {
    void cache.save(data.value)
  }

  const collectionRows = computed<CollectionDeltaRow[]>(() => {
    void revision.value
    return entityRows(data.value, 'UserReleaseEntryStatus') as CollectionDeltaRow[]
  })
  const customListRows = computed<CustomListDeltaRow[]>(() => {
    void revision.value
    return entityRows(data.value, 'UserCustomList') as CustomListDeltaRow[]
  })
  const releaseEntryItemRows = computed<ReleaseEntryItemDeltaRow[]>(() => {
    void revision.value
    return entityRows(data.value, 'UserCustomListReleaseEntryItem') as ReleaseEntryItemDeltaRow[]
  })
  const noveltyItemRows = computed<NoveltyItemDeltaRow[]>(() => {
    void revision.value
    return entityRows(data.value, 'UserCustomListNoveltyItem') as NoveltyItemDeltaRow[]
  })
  const userAdminRow = computed<UserAdminDeltaRow | null>(() => {
    void revision.value
    return (entityRows(data.value, 'UserAdmin')[0] as UserAdminDeltaRow | undefined) ?? null
  })

  function apply(page: UserChangesPage) {
    applyPage(data.value, page as ApplyablePage)
  }

  // Collects every page of a drain WITHOUT applying any of them. A run is all or
  // nothing: entities are spread across pages (a list on one, its membership rows
  // on the next), so applying a half-drain would surface a list whose items are
  // still missing — and the cursor those pages carry would advance past the rows
  // that never arrived, hiding them from every later sync. Null = superseded run.
  async function fetchPagesFrom(
    startCursor: string,
    isCurrent: () => boolean,
  ): Promise<UserChangesPage[] | null> {
    const pages: UserChangesPage[] = []
    let cursor = startCursor
    for (;;) {
      const { data: result } = await apolloClient.query({
        query: GET_USER_CHANGES,
        variables: { sinceSeq: cursor, limit: PAGE_LIMIT },
        fetchPolicy: 'network-only',
        context: BACKGROUND_OPERATION,
      })
      if (!isCurrent()) return null
      const page = result?.userChanges
      if (!page) return null
      pages.push(page)
      if (!page.hasMore) return pages
      // hasMore with a cursor that stands still would buffer pages forever.
      if (page.nextSeq === cursor) return pages
      cursor = page.nextSeq
    }
  }

  async function runSync(): Promise<boolean> {
    const generation = syncGeneration
    const isCurrent = () => generation === syncGeneration
    // A stale replica (server compacted past our cursor) must rebuild from zero.
    const probe = await apolloClient.query({
      query: GET_USER_CHANGES,
      variables: { sinceSeq: data.value.cursor, limit: PAGE_LIMIT },
      fetchPolicy: 'network-only',
      context: BACKGROUND_OPERATION,
    })
    if (!isCurrent()) return false
    const firstPage = probe.data?.userChanges
    if (!firstPage) return false
    if (firstPage.resyncRequired) {
      const pages = await fetchPagesFrom('0', isCurrent)
      if (!pages || !isCurrent()) return false
      data.value.rows = {}
      data.value.cursor = '0'
      for (const page of pages) apply(page)
    } else {
      const rest = firstPage.hasMore ? await fetchPagesFrom(firstPage.nextSeq, isCurrent) : []
      if (!rest || !isCurrent()) return false
      apply(firstPage)
      for (const page of rest) apply(page)
    }
    if (!isCurrent()) return false
    revision.value++
    persist()
    return true
  }

  function ensureCurrentUser() {
    const userId = currentUserId()
    if (data.value.userId !== userId) {
      invalidateSync()
      data.value = emptyData(userId)
      revision.value++
      persist()
    }
  }

  // Resolves to whether server rows were actually applied. A false result means
  // the replica is untouched (skipped while disconnected, signed out, superseded
  // by an account change, or the query failed) — never that the user has no data.
  const sync = async (): Promise<boolean> => {
    if (!infoStore.connected) return false
    if (!currentUserId()) return false
    ensureCurrentUser()
    if (inFlight) return inFlight
    const generation = syncGeneration
    inFlight = (async () => {
      try {
        return await runSync()
      } catch (error) {
        sendError(error, { source: 'user-sync.sync' })
        return false
      } finally {
        // Only clear if no account change spawned a newer run in the meantime.
        if (generation === syncGeneration) inFlight = null
      }
    })()
    return inFlight
  }

  const init = async (): Promise<boolean> => {
    const stored = await cache.load()
    // Discard another account's replica (prevents cross-account leakage).
    const reusable = stored.userId === currentUserId() && isCurrentSchema(stored)
    data.value = reusable ? stored : emptyData(currentUserId())
    loaded.value = true
    revision.value++
    return await sync()
  }

  // Called on logout / account switch so no stale user rows survive.
  const reset = () => {
    invalidateSync()
    data.value = emptyData(currentUserId())
    revision.value++
    persist()
  }

  return {
    loaded,
    hasReplica,
    revision,
    collectionRows,
    customListRows,
    releaseEntryItemRows,
    noveltyItemRows,
    userAdminRow,
    sync,
    init,
    reset,
  }
})
